/**
 * Fade timeline tool. Question script and range model per
 * content/fade-timeline-tool.md. The tool outputs ranges and factors,
 * never a promised date; branch results (red marks, melasma pattern,
 * velvety fold darkening) skip the range entirely.
 *
 * Tan runs on its own model (body area, kind of exposure, whether the sun
 * has stopped). data-mode="tan" starts straight in that flow for the tan
 * fade calculator page; the default mode reaches it from the first question.
 */
(function () {
  var root = document.querySelector('[data-fade-tool]');
  if (!root) return;

  var mode = root.dataset.mode === 'tan' ? 'tan' : 'all';

  var screen = root.querySelector('[data-fade-screen]');
  var resultWrap = root.querySelector('[data-fade-result]');
  var resultBody = root.querySelector('[data-fade-result-body]');
  var gate = root.querySelector('[data-fade-gate]');
  var gateTags = root.querySelector('[data-fade-tags]');
  var products = root.querySelector('[data-fade-products]');

  var LABELS = {
    start: root.dataset.labelStart || 'Start',
    back: root.dataset.labelBack || 'Back',
    restart: root.dataset.labelRestart || 'Start again',
    progress: root.dataset.labelProgress || 'Question {n} of {t}'
  };

  var QUESTIONS = {
    type: {
      prompt: 'What are you trying to fade?',
      options: [
        { v: 'pih', label: 'A dark spot where a pimple was' },
        { v: 'red', label: 'A flat red or purple mark where a pimple was' },
        { v: 'tan', label: 'Overall darkening after time in the sun' },
        { v: 'melasma', label: 'Symmetrical patches on my cheeks, forehead or upper lip' },
        { v: 'body', label: 'Darkness at my neck, elbows, knees or other folds' }
      ]
    },
    velvety: {
      prompt: 'Does the skin there feel velvety or slightly thickened?',
      options: [
        { v: 'no', label: 'No, it feels like the rest of my skin' },
        { v: 'yes', label: 'Yes, the texture is different' }
      ]
    },
    age: {
      prompt: 'How long has it been there?',
      options: [
        { v: 'fresh', label: 'Under 3 months' },
        { v: 'mid', label: '3 months to a year' },
        { v: 'old', label: 'Over a year' }
      ]
    },
    trend: {
      prompt: 'Is it currently getting darker or spreading?',
      options: [
        { v: 'stable', label: 'No, it is stable' },
        { v: 'darkening', label: 'Yes, or I am not sure' }
      ]
    },
    daylight: {
      prompt: 'On a typical day, how much daylight reaches your skin?',
      options: [
        { v: 'indoors', label: 'Mostly indoors, away from windows' },
        { v: 'windows', label: 'Indoors near windows, or short errands outside' },
        { v: 'outdoors', label: 'Outdoors for real stretches of the day' }
      ]
    },
    sunscreen: {
      prompt: 'How often do you wear sunscreen?',
      options: [
        { v: 'daily', label: 'Daily, rain or shine' },
        { v: 'sunny', label: 'Most sunny days' },
        { v: 'rarely', label: 'Rarely' }
      ]
    },
    picking: {
      prompt: 'Do you pick at it or scrub it hard?',
      options: [
        { v: 'no', label: 'No' },
        { v: 'yes', label: 'Sometimes, yes' }
      ]
    },
    tone: {
      prompt: 'How would you describe your skin tone?',
      options: [
        { v: 'light', label: 'Light to medium' },
        { v: 'medium', label: 'Medium to deep' },
        { v: 'deep', label: 'Deep' }
      ]
    },
    area: {
      prompt: 'Where is the tan you most want gone?',
      hint: 'If it is in several places, pick one. Each area fades on its own clock.',
      options: [
        { v: 'face', label: 'My face' },
        { v: 'arms', label: 'Neck, arms or back' },
        { v: 'hands', label: 'Hands or feet' },
        { v: 'legs', label: 'Legs' }
      ]
    },
    exposure: {
      prompt: 'How did you get it?',
      options: [
        { v: 'trip', label: 'A few days of strong sun, like a holiday or a wedding' },
        { v: 'routine', label: 'Everyday sun over weeks or months: commute, sport, outdoor work' }
      ]
    },
    elapsed: {
      prompt: 'When did the heavy sun stop?',
      options: [
        { v: 'ongoing', label: 'It has not. I am still out in it most days' },
        { v: 'recent', label: 'Within the last month' },
        { v: 'months', label: 'One to three months ago' },
        { v: 'long', label: 'More than three months ago' }
      ]
    },
    tan_sunscreen: {
      prompt: 'How often does sunscreen go on that skin now?',
      options: [
        { v: 'daily', label: 'Daily, rain or shine' },
        { v: 'sunny', label: 'On sunny days' },
        { v: 'rarely', label: 'Rarely' }
      ]
    },
    scrub: {
      prompt: 'Are you scrubbing it, or using lemon, bleach or de-tan packs on it?',
      options: [
        { v: 'no', label: 'No, I am leaving it alone' },
        { v: 'yes', label: 'Yes, one or more of those' }
      ]
    },
    age_group: {
      prompt: 'How old are you?',
      options: [
        { v: 'under30', label: 'Under 30' },
        { v: 'mid', label: '30 to 50' },
        { v: 'over50', label: 'Over 50' }
      ]
    }
  };

  var FULL_FLOW = ['age', 'trend', 'daylight', 'sunscreen', 'picking', 'tone'];
  var TAN_FLOW = ['area', 'exposure', 'elapsed', 'tan_sunscreen', 'scrub', 'age_group', 'tone'];

  // Base ranges in weeks [low, high] by branch and age.
  var BASE = {
    pih: { fresh: [6, 12], mid: [12, 24], old: [24, 52] },
    body: { fresh: [12, 24], mid: [16, 32], old: [24, 52] }
  };

  // Tan base ranges in weeks [low, high] by area and exposure, counted from
  // the day the heavy sun stops and assuming daily sunscreen after that.
  var TAN_BASE = {
    face: { trip: [2, 6], routine: [6, 12] },
    arms: { trip: [3, 8], routine: [8, 16] },
    hands: { trip: [4, 10], routine: [10, 20] },
    legs: { trip: [4, 12], routine: [12, 24] }
  };

  // Lower and upper bounds, in weeks, of the time already passed since the
  // sun stopped.
  var ELAPSED_WEEKS = { ongoing: 0, recent: 0, months: 4, long: 13 };
  var ELAPSED_MAX = { ongoing: 0, recent: 4, months: 13, long: Infinity };

  var AREA_NOTES = {
    face: 'Face skin renews faster than anywhere else, which is why a face clears weeks before the arms do.',
    arms: 'Arms and back renew more slowly than the face. Forearms usually lag behind the upper arm because they get more sun.',
    hands: 'Hands lose sunscreen at every wash. Putting it back afterwards, or wearing gloves on a two-wheeler, matters more here than anywhere.',
    legs: 'Legs, and shins most of all, renew more slowly than the rest of the body. That is why a holiday tan lingers there longest.'
  };

  var answers = {};
  var steps = [];
  var stepIndex = 0;

  function track(event, params) {
    if (!window.dataLayer) return;
    var payload = { event: event };
    if (params) for (var k in params) payload[k] = params[k];
    window.dataLayer.push(payload);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function planSteps() {
    if (mode === 'tan') return TAN_FLOW;
    var t = answers.type;
    if (!t) return ['type'];
    if (t === 'red' || t === 'melasma') return ['type'];
    if (t === 'tan') return ['type'].concat(TAN_FLOW);
    if (t === 'body') {
      if (answers.velvety === 'yes') return ['type', 'velvety'];
      return ['type', 'velvety'].concat(FULL_FLOW);
    }
    return ['type'].concat(FULL_FLOW);
  }

  function totalQuestions() {
    var t = answers.type;
    if (mode === 'tan') return TAN_FLOW.length;
    if (t === 'tan') return 1 + TAN_FLOW.length;
    if (t === 'body') return 2 + FULL_FLOW.length;
    return 1 + FULL_FLOW.length;
  }

  function reset() {
    answers = mode === 'tan' ? { type: 'tan' } : {};
    steps = planSteps();
    stepIndex = 0;
  }

  function renderStep() {
    var key = steps[stepIndex];
    var q = QUESTIONS[key];
    screen.innerHTML = '';

    var progress = el(
      'p',
      'fade-tool__progress eyebrow eyebrow--bare',
      LABELS.progress.replace('{n}', stepIndex + 1).replace('{t}', totalQuestions())
    );
    var prompt = el('h3', 'fade-tool__prompt font-display', q.prompt);
    prompt.setAttribute('tabindex', '-1');

    var list = el('div', 'fade-tool__options');
    q.options.forEach(function (opt) {
      var btn = el('button', 'fade-tool__option', opt.label);
      btn.type = 'button';
      if (answers[key] === opt.v) btn.setAttribute('data-selected', 'true');
      btn.addEventListener('click', function () {
        answers[key] = opt.v;
        advance();
      });
      list.appendChild(btn);
    });

    screen.appendChild(progress);
    screen.appendChild(prompt);
    if (q.hint) screen.appendChild(el('p', 'fade-tool__hint', q.hint));
    screen.appendChild(list);

    if (stepIndex > 0) {
      var back = el('button', 'fade-tool__back', LABELS.back);
      back.type = 'button';
      back.addEventListener('click', function () {
        stepIndex -= 1;
        renderStep();
      });
      screen.appendChild(back);
    }

    prompt.focus();
  }

  function advance() {
    steps = planSteps();
    if (stepIndex < steps.length - 1) {
      stepIndex += 1;
      renderStep();
    } else {
      renderResult();
    }
  }

  function formatRange(lowWeeks, highWeeks) {
    if (highWeeks <= 16) {
      return lowWeeks + ' to ' + highWeeks + ' weeks';
    }
    var lowM = Math.max(1, Math.round(lowWeeks / 4.345));
    var highM = Math.round(highWeeks / 4.345);
    if (highM >= 12) return lowM + ' months to a year or more';
    return lowM + ' to ' + highM + ' months';
  }

  function computeRange() {
    var base = BASE[answers.type][answers.age];
    var low = base[0];
    var high = base[1];
    var factors = [];

    if (answers.sunscreen === 'rarely') {
      high *= 1.5;
      factors.push('Your range assumes daily protection. Without it, the clock keeps restarting, and no active outruns daylight.');
    } else if (answers.sunscreen === 'sunny' && answers.daylight === 'outdoors') {
      high *= 1.25;
      factors.push('Outdoor stretches on unprotected days re-trigger production. Cloud cover does not switch this off.');
    }
    if (answers.picking === 'yes') {
      high *= 1.25;
      factors.push('Handling the mark keeps inflammation, and therefore production, active. Hands off moves the date more than any serum.');
    }
    if (answers.tone === 'deep') {
      high *= 1.25;
      factors.push('A more reactive pigment system: marks both fade and re-darken faster than average, so consistency counts double.');
    }
    if (answers.sunscreen === 'daily') {
      factors.push('Daily protection is the single biggest thing you are already doing right. Keep it exactly as it is.');
    }

    return {
      text: formatRange(low, Math.round(high)),
      factors: factors.slice(0, 3),
      capped: answers.age === 'old'
    };
  }

  function computeTanRange() {
    var base = TAN_BASE[answers.area][answers.exposure];
    var low = base[0];
    var high = base[1];
    var factors = [];

    if (answers.tan_sunscreen === 'rarely') {
      high *= 1.5;
      factors.push('This range assumes the tan is no longer being topped up. Without daily sunscreen on that skin it still is, and the clock restarts with every exposure.');
    } else if (answers.tan_sunscreen === 'sunny') {
      high *= 1.25;
      factors.push('Cloudy days and short errands still add to a tan. Sunscreen on sunny days only leaves gaps for it to refill.');
    }
    if (answers.scrub === 'yes') {
      high *= 1.25;
      factors.push('Scrubs, lemon, bleach and de-tan packs irritate skin, and irritated skin makes more pigment, not less. Leaving it alone is the faster route.');
    }
    if (answers.tone === 'deep') {
      high *= 1.25;
      factors.push('Deeper skin builds a tan faster and holds it longer from the same sun. That is the pigment system working as designed, so expect the upper end.');
    }
    if (answers.age_group === 'over50') {
      high *= 1.25;
      factors.push('A tan leaves at the pace skin renews, and renewal slows with age. Past fifty each cycle takes noticeably longer.');
    }
    if (answers.tan_sunscreen === 'daily') {
      factors.push('Daily sunscreen on that skin is what lets a tan fade at all. Keep it exactly as it is.');
    }
    factors.push(AREA_NOTES[answers.area]);

    high = Math.round(high);
    return {
      high: high,
      text: formatRange(low, high),
      factors: factors.slice(0, 3)
    };
  }

  function addMilestones(container) {
    var heading = el('h4', 'fade-tool__subhead eyebrow', 'The twelve-week arc');
    var list = el('ul', 'fade-tool__milestones');
    [
      'Weeks 1 to 4: production slows before appearance changes. Visible progress near zero is normal, not failure.',
      'Weeks 4 to 8: edges soften first, and only on newer marks.',
      'Week 12: the first fair comparison. A photo in the same light beats memory.'
    ].forEach(function (item) {
      list.appendChild(el('li', '', item));
    });
    container.appendChild(heading);
    container.appendChild(list);
  }

  function addTanMilestones(container) {
    var heading = el('h4', 'fade-tool__subhead eyebrow', 'How it usually goes');
    var list = el('ul', 'fade-tool__milestones');
    [
      'The first day or two: part of the darkening was existing pigment darkening in the sun, and that part settles quickly. What remains is the tan proper.',
      'The weeks after: the tan leaves as pigmented cells reach the surface and shed. The face goes first, then the arms, then hands, feet and shins.',
      'Your comparison: check against skin that never sees the sun, like the inside of your upper arm. That is your own tone and where a fading tan is heading. Nothing should go lighter than that.'
    ].forEach(function (item) {
      list.appendChild(el('li', '', item));
    });
    container.appendChild(heading);
    container.appendChild(list);
  }

  function addFactors(container, factors) {
    if (!factors.length) return;
    container.appendChild(el('h4', 'fade-tool__subhead eyebrow', 'What sets your range'));
    var fl = el('ul', 'fade-tool__factors');
    factors.forEach(function (f) {
      fl.appendChild(el('li', '', f));
    });
    container.appendChild(fl);
  }

  // Renders the tan result and returns its analytics branch and whether the
  // email gate applies. A tan that has outlasted its window gets no range.
  function renderTanResult() {
    var range = computeTanRange();
    var elapsed = answers.elapsed;
    var copy = el('div', 'fade-tool__result-copy');

    if (ELAPSED_WEEKS[elapsed] >= range.high) {
      var next =
        mode === 'tan'
          ? 'The <a href="/pages/fade-timeline">pigmentation fade timeline</a> covers the second and third cases.'
          : 'If either of the last two sounds right, start again and pick the description that fits the mark better.';
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'This has outlasted a tan'));
      copy.innerHTML =
        '<p>For skin like yours, a tan usually evens out within ' + range.text + ' once the sun is kept off it. Yours has lasted longer than that, which usually means one of three things.</p>' +
        '<p>It may still be getting sun you are not counting: through a car window, on a steering wheel, on the walk to the bus. The darkening may have settled into pigmentation, which runs on a slower clock. Or it may never have been only a tan.</p>' +
        '<p>' + next + ' If the patch is spreading, changing shape, or sits in matching patches on both cheeks, a dermatologist is the better first step.</p>';
      resultBody.appendChild(copy);
      return { branch: 'tan-outlasted', gate: false };
    }

    if (elapsed === 'ongoing') {
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'Your clock has not started yet'));
      copy.innerHTML =
        '<p>A tan cannot fade while it is still being topped up, and sunscreen slows a tan without stopping one (<a href="/blogs/cham-journal/does-sunscreen-prevent-tanning">why that is</a>). From the day the heavy sun stops, a realistic window for this tan is <strong>' + range.text + '</strong>.</p>' +
        '<p>That assumes daily protection from then on. It is a range, not a promise: biology does not sign contracts.</p>';
    } else {
      var already = '';
      if (elapsed === 'months') already = ', so you are already one to three months in';
      if (elapsed === 'long') already = ', so you are more than three months in and into the later part of it';
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'A realistic window: ' + range.text));
      copy.innerHTML =
        '<p>That counts from the day the heavy sun stopped' + already + '. It assumes daily protection from here on. It is a range, not a promise: biology does not sign contracts.</p>';
    }
    resultBody.appendChild(copy);

    if (ELAPSED_MAX[elapsed] > range.high) {
      var late = el('div', 'fade-tool__caution');
      late.innerHTML =
        '<p>You may already be past the end of this window. If the tan has not changed at all by now, it is probably still getting sun, or it has settled into pigmentation, which fades on a slower clock.</p>';
      resultBody.appendChild(late);
    }

    addFactors(resultBody, range.factors);
    addTanMilestones(resultBody);
    return { branch: elapsed === 'ongoing' ? 'tan-ongoing' : 'tan', gate: true };
  }

  function renderResult() {
    var t = answers.type;
    var branch = t;
    var showGate = false;
    resultBody.innerHTML = '';
    screen.hidden = true;
    resultWrap.hidden = false;
    gate.hidden = true;
    if (products) products.hidden = true;

    if (t === 'tan') {
      var tan = renderTanResult();
      branch = tan.branch;
      showGate = tan.gate;
    } else if (t === 'melasma') {
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'This pattern follows different rules'));
      var m = el('div', 'fade-tool__result-copy');
      m.innerHTML =
        '<p>Symmetrical patches across the cheeks, forehead or upper lip usually are not a cosmetic project. That pattern follows melasma’s rules: hormones and heat are inputs, and the right first step is a dermatologist, not a serum.</p>' +
        '<p>What a routine can honestly do is protect. Daily sunscreen matters more for this pattern than for any other on this page. The full picture is in <a href="/blogs/cham-journal/melasma-where-cosmetics-stop">melasma: where cosmetics stop and dermatology begins</a>, and the <a href="/pages/melasma-planner">melasma season planner</a> maps when in the Indian year a dermatologist-led plan gets its cleanest run.</p>';
      resultBody.appendChild(m);
    } else if (t === 'red') {
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'This is probably not a pigment problem'));
      var r = el('div', 'fade-tool__result-copy');
      r.innerHTML =
        '<p>A flat red or purple mark has no excess pigment in it, so nothing that acts on pigment will act on it. It is leftover blood-vessel activity from the inflammation, and it resolves with time, a calm barrier and sun protection.</p>' +
        '<p>The strongest move is to stop treating it. How to tell these marks apart is covered in <a href="/blogs/cham-journal/acne-marks-vs-acne-scars">acne marks vs acne scars</a>, and the barrier side in <a href="/blogs/cham-journal/skin-barrier-repair-guide">the barrier repair guide</a>.</p>';
      resultBody.appendChild(r);
    } else if (t === 'body' && answers.velvety === 'yes') {
      branch = 'referral-body';
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'This one is worth a proper look'));
      var v = el('div', 'fade-tool__result-copy');
      v.innerHTML =
        '<p>Velvety, thickened, symmetrical darkening in body folds often has internal causes, and a cream alone does not address those. This is not an alarm, it is a pattern worth showing to a doctor, who can check the common drivers properly.</p>' +
        '<p>No product recommendation belongs on this result, so we are not making one. The <a href="/pages/body-map">body zone map</a> walks this pattern, and the six other body zones, cause by cause.</p>';
      resultBody.appendChild(v);
    } else {
      var range = computeRange();
      resultBody.appendChild(el('h3', 'fade-tool__result-heading font-display', 'A realistic window: ' + range.text));
      var intro = el('div', 'fade-tool__result-copy');
      var caveat = range.capped
        ? ' An old, dark mark may lighten substantially without vanishing completely, and that is a normal outcome, not a failed one.'
        : '';
      intro.innerHTML =
        '<p>That assumes daily protection and a consistent routine. It is a range, not a promise: biology does not sign contracts.' +
        caveat +
        '</p>';
      resultBody.appendChild(intro);

      if (answers.trend === 'darkening') {
        var warn = el('div', 'fade-tool__caution');
        warn.innerHTML =
          '<p>Something is still triggering production. Until that stops, no range holds. If nothing obvious explains the darkening, or the patch is changing shape, have it looked at rather than treated.</p>';
        resultBody.appendChild(warn);
      }

      addFactors(resultBody, range.factors);
      addMilestones(resultBody);
      showGate = true;
    }

    if (showGate) {
      if (gateTags) {
        gateTags.value = 'fade-plan,fade-plan-' + t;
      }
      gate.hidden = false;
      if (products) products.hidden = false;
    }

    var restart = el('button', 'fade-tool__back', LABELS.restart);
    restart.type = 'button';
    restart.addEventListener('click', function () {
      reset();
      resultWrap.hidden = true;
      screen.hidden = false;
      renderStep();
    });
    resultBody.appendChild(restart);

    resultBody.setAttribute('tabindex', '-1');
    resultBody.focus();
    track('fade_tool_complete', { fade_branch: branch, fade_mode: mode });
  }

  var startBtn = root.querySelector('[data-fade-start]');
  if (startBtn) {
    startBtn.addEventListener('click', function () {
      root.querySelector('[data-fade-intro]').hidden = true;
      screen.hidden = false;
      reset();
      renderStep();
      track('fade_tool_start', { fade_mode: mode });
    });
  }

  var gateForm = gate ? gate.querySelector('form') : null;
  if (gateForm) {
    gateForm.addEventListener('submit', function () {
      track('fade_tool_capture', { fade_branch: answers.type, fade_mode: mode });
    });
  }
})();
